import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

const CORE_DATA_COLUMNS = [
  "p_grade",
  "moisture",
  "slimes",
  "floatability",
  "ore_classification",
  "stockpile_routing",
];

const DEFAULT_INDICATOR_COLOR = "#90a4ae";
const ORE_LUMPY_UNLIBERATED_COLOR = "#d32f2f";
const ORE_LUMPY_SEMI_LIBERATED_COLOR = "#f06292";

const INDICATOR_CONFIG = [
  {
    key: "p_grade",
    label: "P-grade",
    color: "#90caf9",
    formatValue: (value) => formatNumber(value, { maximumFractionDigits: 2 }),
    displayAsCircle: false,
  },
  {
    key: "moisture",
    label: "Moisture",
    getColor: (value) => {
      const normalised = normaliseText(value);
      if (normalised === "low") {
        return "#43a047";
      }
      if (normalised === "medium") {
        return "#fb8c00";
      }
      if (normalised === "high") {
        return "#e53935";
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
  {
    key: "floatability",
    label: "Floatability",
    getColor: (value) => {
      const normalised = normaliseText(value);
      if (normalised === "non-floatable") {
        return "#e53935";
      }
      if (normalised === "moderate floatable") {
        return "#fb8c00";
      }
      if (normalised === "floatable") {
        return "#43a047";
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
  {
    key: "ore_classification",
    label: "Ore classification",
    getColor: (value) => {
      const normalised = normaliseText(value).replace(/[–-]/g, " ");
      if (normalised === "lumpy unliberated") {
        return ORE_LUMPY_UNLIBERATED_COLOR;
      }
      if (normalised === "lumpy semi liberated") {
        return ORE_LUMPY_SEMI_LIBERATED_COLOR;
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
  {
    key: "hours_remaining",
    label: "Hours remaining",
    color: "#8e24aa",
    formatValue: formatHours,
    shouldDisplay: (value) => hasValue(value),
    displayAsCircle: false,
  },
  {
    key: "product_classification",
    label: "Product classification",
    getColor: (value) => {
      const normalised = normaliseText(value);
      if (normalised === "low concentrate") {
        return "#e53935";
      }
      if (normalised === "medium concentrate") {
        return "#fb8c00";
      }
      if (normalised === "high concentrate") {
        return "#43a047";
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
  {
    key: "recovery_classification",
    label: "Recovery classification",
    getColor: (value) => {
      const normalised = normaliseText(value);
      if (normalised === "low") {
        return "#e53935";
      }
      if (normalised === "medium") {
        return "#fb8c00";
      }
      if (normalised === "high") {
        return "#43a047";
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
  {
    key: "mass_pull_classification",
    label: "Mass pull classification",
    getColor: (value) => {
      const normalised = normaliseText(value);
      if (normalised === "low") {
        return "#e53935";
      }
      if (normalised === "medium") {
        return "#fb8c00";
      }
      if (normalised === "high") {
        return "#43a047";
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
  {
    key: "slimes_classification",
    label: "Slimes classification",
    getColor: (value) => {
      const normalised = normaliseText(value);
      if (normalised === "low") {
        return "#43a047";
      }
      if (normalised === "medium") {
        return "#fb8c00";
      }
      if (normalised === "high") {
        return "#e53935";
      }
      return DEFAULT_INDICATOR_COLOR;
    },
    formatValue: formatText,
  },
];

const INDICATOR_LOOKUP = new Map(
  INDICATOR_CONFIG.map((indicator) => [indicator.key, indicator])
);

const INDICATOR_LEGEND_GROUPS = [
  {
    title: "Moisture",
    items: [
      { label: "Low", color: "#43a047" },
      { label: "Medium", color: "#fb8c00" },
      { label: "High", color: "#e53935" },
    ],
  },
  {
    title: "Floatability",
    items: [
      { label: "Floatable", color: "#43a047" },
      { label: "Moderate floatable", color: "#fb8c00" },
      { label: "Non-floatable", color: "#e53935" },
    ],
  },
  {
    title: "Product classification",
    items: [
      { label: "High concentrate", color: "#43a047" },
      { label: "Medium concentrate", color: "#fb8c00" },
      { label: "Low concentrate", color: "#e53935" },
    ],
  },
  {
    title: "Recovery classification",
    items: [
      { label: "High", color: "#43a047" },
      { label: "Medium", color: "#fb8c00" },
      { label: "Low", color: "#e53935" },
    ],
  },
  {
    title: "Mass pull classification",
    items: [
      { label: "High", color: "#43a047" },
      { label: "Medium", color: "#fb8c00" },
      { label: "Low", color: "#e53935" },
    ],
  },
  {
    title: "Slimes classification",
    items: [
      { label: "Low", color: "#43a047" },
      { label: "Medium", color: "#fb8c00" },
      { label: "High", color: "#e53935" },
    ],
  },
  {
    title: "Ore classification",
    items: [
      { label: "Lumpy unliberated", color: ORE_LUMPY_UNLIBERATED_COLOR },
      { label: "Lumpy semi-liberated", color: ORE_LUMPY_SEMI_LIBERATED_COLOR },
      { label: "Other classifications", color: DEFAULT_INDICATOR_COLOR },
    ],
  },
  {
    title: "No data",
    items: [{ label: "Awaiting data", color: DEFAULT_INDICATOR_COLOR }],
  },
];

const MODAL_TRANSITION_MS = 320;

const FINGER_STATUS_OPTIONS = [
  { value: "wash plant", label: "Wash Plant" },
  { value: "being built", label: "Being Built" },
  { value: "depleted", label: "Depleted" },
  { value: "built", label: "Built" },
  { value: "fed", label: "Fed" },
  { value: "main plant", label: "Main Plant" },
  { value: "blending tracking", label: "Blending tracking" },
  { value: "other", label: "Other statuses" },
];

const DEFAULT_STATUS_FILTER = FINGER_STATUS_OPTIONS.filter(
  (option) => option.value !== "depleted"
).map((option) => option.value);

function normaliseText(value) {
  if (!hasValue(value)) {
    return "";
  }
  return `${value}`.trim().toLowerCase();
}

function isDepletedStatus(value) {
  return normaliseText(value).includes("deplet");
}

function hasValue(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return true;
}

function parseNumeric(value) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const text = `${value}`.trim();
  if (text === "") {
    return null;
  }

  const normalised = text.replace(/,/g, "");
  const direct = Number(normalised);
  if (!Number.isNaN(direct)) {
    return direct;
  }

  const match = normalised.match(/-?\d+(?:\.\d+)?/);
  if (!match) {
    return null;
  }

  const fallback = Number(match[0]);
  return Number.isNaN(fallback) ? null : fallback;
}

function normaliseKeyText(value) {
  if (!hasValue(value)) {
    return null;
  }

  const text = `${value}`.trim();
  if (text === "") {
    return null;
  }

  return text.toLowerCase();
}

function sanitiseFingerOrderInput(order) {
  if (!Array.isArray(order)) {
    return [];
  }

  const seen = new Set();
  const result = [];

  order.forEach((value) => {
    if (!hasValue(value)) {
      return;
    }

    const text = `${value}`.trim();
    if (!text) {
      return;
    }

    const normalised = normaliseKeyText(text);
    if (!normalised || seen.has(normalised)) {
      return;
    }

    seen.add(normalised);
    result.push(text);
  });

  return result;
}

function getFingerOrderKey(finger) {
  if (!finger) {
    return null;
  }

  if (hasValue(finger.key)) {
    return String(finger.key).trim();
  }

  if (hasValue(finger.orderKey)) {
    return String(finger.orderKey).trim();
  }

  if (hasValue(finger.fingerLabel)) {
    return String(finger.fingerLabel).trim();
  }

  if (Number.isFinite(finger.finger)) {
    return String(Math.trunc(finger.finger));
  }

  return null;
}

function reorderFingerOrder(order, sourceKey, targetKey, position = "before") {
  if (!Array.isArray(order) || !hasValue(sourceKey)) {
    return Array.isArray(order) ? [...order] : [];
  }

  const source = `${sourceKey}`.trim();
  const target = hasValue(targetKey) ? `${targetKey}`.trim() : null;
  const next = order.filter((value) => `${value}`.trim() !== source);

  if (!target) {
    next.push(source);
    return next;
  }

  const targetIndex = next.findIndex((value) => `${value}`.trim() === target);
  if (targetIndex === -1) {
    next.push(source);
    return next;
  }

  const insertIndex = position === "after" ? targetIndex + 1 : targetIndex;
  next.splice(insertIndex, 0, source);
  return next;
}

function arraysEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return false;
    }
  }

  return true;
}

function clampIndex(value, min, max) {
  if (!Number.isFinite(value)) {
    return min;
  }

  if (value <= min) {
    return min;
  }

  if (value >= max) {
    return max;
  }

  return value;
}

function toDateValue(value) {
  if (!hasValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }

  const text = `${value}`.trim();
  if (text === "") {
    return null;
  }

  const isoCandidate = text.includes("T") ? text : text.replace(" ", "T");
  const timestamp = Date.parse(isoCandidate);
  if (!Number.isNaN(timestamp)) {
    return timestamp;
  }

  const fallback = Date.parse(text);
  return Number.isNaN(fallback) ? null : fallback;
}

function formatText(value) {
  if (!hasValue(value)) {
    return "—";
  }
  return `${value}`.trim();
}

function formatNumber(value, options = {}) {
  if (!hasValue(value)) {
    return "—";
  }

  const numericValue = parseNumeric(value);
  if (numericValue === null) {
    const textValue = `${value}`.trim();
    return textValue === "" ? "—" : textValue;
  }

  const formatOptions = { maximumFractionDigits: 0, ...options };
  return numericValue.toLocaleString("en-ZA", formatOptions);
}

function formatPercentage(value) {
  if (!hasValue(value)) {
    return "—";
  }

  const numericValue = parseNumeric(value);
  if (numericValue === null) {
    const textValue = `${value}`.trim();
    return textValue === "" ? "—" : textValue;
  }

  return `${numericValue.toFixed(0)}%`;
}

function formatRecovery(value) {
  if (!hasValue(value)) {
    return "—";
  }

  const numericValue = parseNumeric(value);
  if (numericValue === null) {
    const textValue = `${value}`.trim();
    return textValue === "" ? "—" : textValue;
  }

  const digits = Number.isInteger(numericValue) ? 0 : 1;
  return `${numericValue.toFixed(digits)}%`;
}

function formatHours(value) {
  if (!hasValue(value)) {
    return "—";
  }

  const numericValue = parseNumeric(value);
  if (numericValue === null) {
    const textValue = `${value}`.trim();
    return textValue === "" ? "—" : textValue;
  }

  const digits = Math.abs(numericValue) >= 10 || Number.isInteger(numericValue) ? 0 : 1;
  return `${numericValue.toFixed(digits)} h`;
}

function formatHoursTotal(value) {
  const numericValue = parseNumeric(value);
  if (numericValue === null || !Number.isFinite(numericValue)) {
    return "—";
  }

  const absolute = Math.abs(numericValue);
  const digits = absolute >= 10 || Number.isInteger(numericValue) ? 0 : 1;
  return `${numericValue.toFixed(digits)} H`;
}

function formatDurationSummary(value) {
  const numericValue = parseNumeric(value);
  if (numericValue === null || !Number.isFinite(numericValue)) {
    return "—";
  }

  const clamped = Math.max(numericValue, 0);
  const totalMinutes = Math.round(clamped * 60);
  if (totalMinutes <= 0) {
    return "0H";
  }

  const MINUTES_PER_HOUR = 60;
  const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
  const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

  let remainder = totalMinutes;
  const weeks = Math.floor(remainder / MINUTES_PER_WEEK);
  remainder -= weeks * MINUTES_PER_WEEK;
  const days = Math.floor(remainder / MINUTES_PER_DAY);
  remainder -= days * MINUTES_PER_DAY;

  const hoursDecimal = remainder / MINUTES_PER_HOUR;
  const components = [];

  if (weeks > 0) {
    components.push(`${weeks}W`);
  }

  if (days > 0) {
    components.push(`${days}D`);
  }

  const roundedHours = Math.round(hoursDecimal * 10) / 10;
  if (roundedHours > 0 || components.length === 0) {
    const formattedHours = Number.isInteger(roundedHours)
      ? `${roundedHours}`
      : `${roundedHours.toFixed(1)}`.replace(/\.0$/, "");
    components.push(`${formattedHours}H`);
  }

  return components.join(" ");
}

function clampPercentageValue(value) {
  const numericValue = parseNumeric(value);
  if (numericValue === null || !Number.isFinite(numericValue)) {
    return null;
  }

  const clamped = Math.min(Math.max(numericValue, 0), 100);
  const rounded = Math.round(clamped * 10) / 10;
  return Number.isFinite(rounded) ? rounded : null;
}

function formatDate(value) {
  if (!hasValue(value)) {
    return "—";
  }

  const text = `${value}`.trim();
  const timestamp = toDateValue(value);
  if (timestamp === null) {
    return text === "" ? "—" : text;
  }

  const hasTime = /[T\s]\d{2}:\d{2}/.test(text);
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return text === "" ? "—" : text;
  }

  if (hasTime) {
    return date.toLocaleString("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }

  return date.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getBlockRelevantTimestamp(block) {
  if (!block) {
    return null;
  }

  const candidates = [
    block.latest_update_value,
    block.allocation_date_value,
    block.metallurgist_date_value,
    block.geologist_date_value,
  ];

  const timestamps = candidates
    .map((value) => (Number.isFinite(value) ? value : null))
    .filter((value) => value !== null);

  if (!timestamps.length) {
    return null;
  }

  return Math.max(...timestamps);
}

function normaliseBlock(rawBlock) {
  if (!rawBlock || typeof rawBlock !== "object") {
    return null;
  }

  const blockId = hasValue(rawBlock.block_id) ? String(rawBlock.block_id).trim() : "";
  if (!blockId) {
    return null;
  }

  const fingerValue = rawBlock.finger_number ?? null;
  const fingerNumeric = parseNumeric(fingerValue);
  const fingerNumber = Number.isFinite(fingerNumeric) ? Math.trunc(fingerNumeric) : null;
  let fingerLabel = null;
  if (hasValue(rawBlock.finger_label)) {
    fingerLabel = String(rawBlock.finger_label).trim();
  } else if (hasValue(fingerValue)) {
    fingerLabel = String(fingerValue).trim();
  } else if (fingerNumber !== null) {
    fingerLabel = String(fingerNumber);
  }

  let fingerKey = null;
  if (hasValue(rawBlock.finger_key)) {
    fingerKey = String(rawBlock.finger_key).trim();
  } else if (fingerLabel) {
    fingerKey = fingerLabel;
  } else if (fingerNumber !== null) {
    fingerKey = String(fingerNumber);
  }

  return {
    ...rawBlock,
    block_id: blockId,
    finger_number: fingerNumber,
    finger_label: fingerLabel,
    finger_key: fingerKey,
    tonnage_value: parseNumeric(rawBlock.tonnage ?? null),
    recovery_value: parseNumeric(rawBlock.recovery ?? null),
    latest_update: rawBlock.latest_update ?? null,
    latest_update_value: toDateValue(rawBlock.latest_update ?? null),
    geologist_date_value: toDateValue(rawBlock.geologist_date ?? null),
    metallurgist_date_value: toDateValue(rawBlock.metallurgist_date ?? null),
    allocation_date_value: toDateValue(rawBlock.allocation_date ?? null),
  };
}

function normaliseFinger(rawFinger) {
  if (!rawFinger || typeof rawFinger !== "object") {
    return null;
  }

  const labelCandidates = [];
  if (hasValue(rawFinger.finger_label)) {
    labelCandidates.push(String(rawFinger.finger_label).trim());
  }
  if (hasValue(rawFinger.finger_nr)) {
    labelCandidates.push(String(rawFinger.finger_nr).trim());
  }

  let fingerLabel = labelCandidates.find((label) => label) || null;
  const fingerNumericSource = hasValue(rawFinger.finger_number)
    ? rawFinger.finger_number
    : fingerLabel;
  const fingerNumeric = parseNumeric(fingerNumericSource);
  const fingerNumber = Number.isFinite(fingerNumeric) ? Math.trunc(fingerNumeric) : null;

  if (!fingerLabel && fingerNumber !== null) {
    fingerLabel = String(fingerNumber);
  }

  let fingerKey = null;
  if (hasValue(rawFinger.finger_key)) {
    fingerKey = String(rawFinger.finger_key).trim();
  } else if (fingerLabel) {
    fingerKey = fingerLabel;
  } else if (fingerNumber !== null) {
    fingerKey = String(fingerNumber);
  }

  if (!fingerKey) {
    return null;
  }

  const percentageFillValue = parseNumeric(
    rawFinger.percentage_fill_value ?? rawFinger.percentage_fill ?? null
  );
  const resolvedFill = Number.isFinite(percentageFillValue) ? percentageFillValue : null;

  return {
    ...rawFinger,
    finger_label: fingerLabel,
    finger_number: fingerNumber,
    finger_key: fingerKey,
    finger_status: hasValue(rawFinger.finger_status)
      ? String(rawFinger.finger_status).trim()
      : null,
    percentage_fill_value: resolvedFill,
    latest_update: rawFinger.latest_update ?? null,
    latest_update_value: toDateValue(rawFinger.latest_update ?? null),
  };
}

function resolveIndicatorColor(indicator, block) {
  if (!indicator) {
    return DEFAULT_INDICATOR_COLOR;
  }

  if (typeof indicator.getColor === "function") {
    const resolved = indicator.getColor(block[indicator.key], block);
    return resolved || DEFAULT_INDICATOR_COLOR;
  }

  if (indicator.color) {
    return indicator.color;
  }

  return DEFAULT_INDICATOR_COLOR;
}

function getOreClassificationKey(block) {
  if (!block || !hasValue(block.ore_classification)) {
    return "";
  }

  const normalised = normaliseText(block.ore_classification)
    .replace(/[–-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalised) {
    return "";
  }

  if (normalised === "free flowing sand") {
    return "free-flowing-sand";
  }

  if (normalised === "lumpy unliberated") {
    return "lumpy-unliberated";
  }

  if (normalised === "lumpy semi liberated") {
    return "lumpy-semi-liberated";
  }

  return "";
}

const NEUTRAL_PILL_COLOR = "rgba(144, 164, 174, 0.24)";

function getPillVisualStyles(color, options = {}) {
  const { backgroundAlpha = 0.16, borderAlpha = 0.38, textColor } = options;
  const rgb = parseColorToRgb(color);

  if (!rgb) {
    return {
      background: NEUTRAL_PILL_COLOR,
      border: "rgba(144, 164, 174, 0.32)",
      text: textColor || getReadableTextColor(color),
    };
  }

  const accent = textColor || `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;

  return {
    background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${backgroundAlpha})`,
    border: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${borderAlpha})`,
    text: accent,
  };
}

function parseColorToRgb(color) {
  if (!color) {
    return null;
  }

  const normalised = `${color}`.trim();
  const hexMatch = normalised.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].every((channel) => Number.isFinite(channel))) {
        return { r, g, b };
      }
    }
  }

  const rgbMatch = normalised.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbMatch) {
    const parts = rgbMatch[1]
      .split(",")
      .map((segment) => parseFloat(segment.trim()))
      .filter((value, index) => index < 3 && Number.isFinite(value));

    if (parts.length === 3) {
      const [r, g, b] = parts.map((value) => Math.max(0, Math.min(255, value)));
      return { r, g, b };
    }
  }

  return null;
}

function getReadableTextColor(color) {
  const rgb = parseColorToRgb(color);
  if (!rgb) {
    return "#0f1720";
  }

  const toLinear = (channel) => {
    const normalised = channel / 255;
    return normalised <= 0.03928
      ? normalised / 12.92
      : Math.pow((normalised + 0.055) / 1.055, 2.4);
  };

  const luminance =
    0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);

  return luminance > 0.55 ? "#0f1720" : "#ffffff";
}

function blockHasCoreData(block) {
  return CORE_DATA_COLUMNS.some((column) => hasValue(block[column]));
}

function getFingerStatusKey(status) {
  const normalised = normaliseText(status);

  if (!normalised) {
    return "active";
  }

  if (normalised.includes("deplet")) {
    return "depleted";
  }

  if (normalised.includes("wash") && normalised.includes("plant")) {
    return "plant";
  }

  if (normalised.includes("main") && normalised.includes("plant")) {
    return "plant";
  }

  if (normalised.includes("blend")) {
    return "blending";
  }

  if (normalised.includes("being") && normalised.includes("built")) {
    return "building";
  }

  if (normalised.includes("fed")) {
    return "fed";
  }

  if (normalised.includes("built")) {
    return "built";
  }

  if (normalised.includes("idle") || normalised.includes("standby")) {
    return "idle";
  }

  return "active";
}

function getBlockStatusKey(status) {
  const normalised = normaliseText(status);

  if (!normalised) {
    return "";
  }

  if (normalised.includes("deplet")) {
    return "depleted";
  }

  return "";
}

const RomPad = () => {
  const [blocks, setBlocks] = useState([]);
  const [fingers, setFingers] = useState([]);
  const [fingerOrder, setFingerOrder] = useState([]);
  const [fingerOrderUpdatedAt, setFingerOrderUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [selectedFingerKey, setSelectedFingerKey] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [activeBlock, setActiveBlock] = useState(null);
  const [activeFinger, setActiveFinger] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFingerModalOpen, setIsFingerModalOpen] = useState(false);
  const [fingerFillInitial, setFingerFillInitial] = useState(null);
  const [fingerFillDraft, setFingerFillDraft] = useState(null);
  const [fingerFillSaving, setFingerFillSaving] = useState(false);
  const [fingerFillFeedback, setFingerFillFeedback] = useState(null);
  const [fingerOrderSaving, setFingerOrderSaving] = useState(false);
  const [fingerOrderFeedback, setFingerOrderFeedback] = useState(null);
  const [showFingerOrderUpdatedMeta, setShowFingerOrderUpdatedMeta] = useState(false);
  const [draggingFingerKey, setDraggingFingerKey] = useState(null);
  const [fingerDropIndicator, setFingerDropIndicator] = useState(null);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTER);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [includeDepletedBlocks, setIncludeDepletedBlocks] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const modalTransitionDuration = prefersReducedMotion ? 0 : MODAL_TRANSITION_MS;
  const statusOptionValues = useMemo(
    () => new Set(FINGER_STATUS_OPTIONS.map((option) => option.value)),
    []
  );
  const fingerOrderRequestIdRef = useRef(0);
  const fingerOrderFeedbackTimerRef = useRef(null);
  const [modalContainer] = useState(() => {
    if (typeof document === "undefined") {
      return null;
    }

    const element = document.createElement("div");
    element.className = "rom-pad__modal-root";
    return element;
  });

  const fetchRomPadData = useCallback(
    async ({ signal } = {}) => {
      const response = await fetch("/api/index.php?resource=rom-pad-blocks", {
        signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const payload = await response.json();

      if (!payload || payload.status !== "ok") {
        const message = payload?.message || "Unexpected API response.";
        throw new Error(message);
      }

      const rawBlocks = Array.isArray(payload?.data?.blocks) ? payload.data.blocks : [];
      const rawFingers = Array.isArray(payload?.data?.fingers) ? payload.data.fingers : [];
      const rawOrderPayload = payload?.data?.finger_order || null;
      const rawOrder = Array.isArray(rawOrderPayload?.order) ? rawOrderPayload.order : [];
      const rawOrderUpdatedAt = hasValue(rawOrderPayload?.updated_at)
        ? String(rawOrderPayload.updated_at).trim()
        : null;

      const processedBlocks = rawBlocks
        .map((item) => normaliseBlock(item))
        .filter(
          (block) =>
            block &&
            block.block_id &&
            block.finger_number !== null &&
            block.finger_number !== undefined
        );

      const processedFingers = rawFingers
        .map((item) => normaliseFinger(item))
        .filter((finger) => finger && finger.finger_key);

      return {
        blocks: processedBlocks,
        fingers: processedFingers,
        fingerOrder: sanitiseFingerOrderInput(rawOrder),
        fingerOrderUpdatedAt: rawOrderUpdatedAt,
      };
    },
    []
  );

  const refreshRomPadData = useCallback(async () => {
    const data = await fetchRomPadData();
    setBlocks(data.blocks);
    setFingers(data.fingers);
    setFingerOrder(data.fingerOrder);
    setFingerOrderUpdatedAt(data.fingerOrderUpdatedAt || null);
    return data;
  }, [fetchRomPadData]);

  const filterPanelId = useId();
  const legendId = useId();
  const legendDescriptionId = useId();
  const legendIconGradientRawId = useId();
  const legendDialogId = useId();
  const legendIconGradientId = useMemo(() => {
    return `rom-pad-legend-icon-${legendIconGradientRawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  }, [legendIconGradientRawId]);
  const filterPanelRef = useRef(null);
  const filterToggleRef = useRef(null);
  const legendButtonRef = useRef(null);

  const handleCloseLegend = useCallback(() => {
    setIsLegendOpen(false);
    if (legendButtonRef.current) {
      legendButtonRef.current.focus({ preventScroll: true });
    }
  }, [legendButtonRef, setIsLegendOpen]);

  const handleOpenLegend = useCallback(() => {
    setIsLegendOpen(true);
    setIsFilterPanelOpen(false);
  }, [setIsFilterPanelOpen, setIsLegendOpen]);

  useEffect(() => {
    if (!isFilterPanelOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target) &&
        filterToggleRef.current &&
        !filterToggleRef.current.contains(event.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsFilterPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isFilterPanelOpen]);

  useEffect(() => {
    if (!modalContainer) {
      return undefined;
    }

    document.body.appendChild(modalContainer);

    return () => {
      if (modalContainer.parentNode) {
        modalContainer.parentNode.removeChild(modalContainer);
      }
    };
  }, [modalContainer]);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const loadBlocks = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchRomPadData({ signal: controller.signal });
        if (!isActive) {
          return;
        }

        setBlocks(data.blocks);
        setFingers(data.fingers);
        setFingerOrder(data.fingerOrder);
      } catch (fetchError) {
        if (!isActive || fetchError.name === "AbortError") {
          return;
        }

        setBlocks([]);
        setFingers([]);
        setFingerOrder([]);
        setError(fetchError.message || "Unable to fetch ROM pad data.");
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadBlocks();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [fetchRomPadData]);

  useEffect(() => {
    if (!blocks.length && !fingers.length) {
      return;
    }

    const frame = requestAnimationFrame(() => setIsMounted(true));
    return () => cancelAnimationFrame(frame);
  }, [blocks.length, fingers.length]);

  useEffect(() => {
    if (!selectedBlockId) {
      return;
    }

    const exists = blocks.some((block) => block.block_id === selectedBlockId);
    if (!exists) {
      setSelectedBlockId(null);
    }
  }, [blocks, selectedBlockId]);

  const { groupedFingers, resolvedFingerOrder, fingerOrderLookup } = useMemo(() => {
    if (!blocks.length && !fingers.length) {
      return {
        groupedFingers: [],
        resolvedFingerOrder: [],
        fingerOrderLookup: new Map(),
      };
    }

    const map = new Map();

    const buildCandidates = (values) => {
      return Array.from(
        new Set(
          values
            .map((value) => {
              if (value === null || value === undefined) {
                return null;
              }
              const text = `${value}`.trim();
              return text === "" ? null : text;
            })
            .filter(Boolean)
        )
      );
    };

    const ensureEntry = (primaryKey, { fingerInfo = null, label = null, number = null } = {}) => {
      if (!primaryKey) {
        return null;
      }

      const normalised = normaliseKeyText(primaryKey);
      if (!normalised) {
        return null;
      }

      let entry = map.get(normalised);
      if (!entry) {
        entry = {
          key: primaryKey,
          fingerLabel: label || primaryKey,
          finger: Number.isFinite(number) ? Math.trunc(number) : null,
          fingerInfo: null,
          blocks: [],
        };
        map.set(normalised, entry);
      }

      if (fingerInfo && !entry.fingerInfo) {
        entry.fingerInfo = fingerInfo;
        if (hasValue(fingerInfo.finger_key)) {
          entry.key = String(fingerInfo.finger_key).trim();
        }
        if (hasValue(fingerInfo.finger_label)) {
          entry.fingerLabel = String(fingerInfo.finger_label).trim();
        }
        if (Number.isFinite(fingerInfo.finger_number)) {
          entry.finger = Math.trunc(fingerInfo.finger_number);
        }
      } else {
        if (!entry.fingerLabel && label) {
          entry.fingerLabel = label;
        }
        if ((entry.finger === null || entry.finger === undefined) && Number.isFinite(number)) {
          entry.finger = Math.trunc(number);
        }
      }

      return entry;
    };

    const registerAliases = (entry, aliases) => {
      aliases.forEach((alias) => {
        const aliasKey = normaliseKeyText(alias);
        if (!aliasKey) {
          return;
        }
        if (!map.has(aliasKey)) {
          map.set(aliasKey, entry);
        }
      });
    };

    fingers.forEach((finger) => {
      if (!finger) {
        return;
      }

      const candidates = buildCandidates([
        finger.finger_key,
        finger.finger_label,
        finger.finger_nr,
        Number.isFinite(finger.finger_number) ? String(finger.finger_number) : null,
      ]);

      if (!candidates.length) {
        return;
      }

      const entry = ensureEntry(candidates[0], { fingerInfo: finger });
      if (entry) {
        registerAliases(entry, candidates);
      }
    });

    blocks.forEach((block) => {
      const candidates = buildCandidates([
        block.finger_key,
        block.finger_label,
        block.finger_number !== null && block.finger_number !== undefined
          ? String(block.finger_number)
          : null,
      ]);

      if (!candidates.length) {
        return;
      }

      let entry = null;

      for (const candidate of candidates) {
        const normalised = normaliseKeyText(candidate);
        if (normalised && map.has(normalised)) {
          entry = map.get(normalised);
          break;
        }
      }

      if (!entry) {
        const numericCandidate = block.finger_number ?? parseNumeric(candidates[0]);
        entry = ensureEntry(candidates[0], {
          label: candidates[0],
          number: Number.isFinite(numericCandidate) ? numericCandidate : null,
        });
        if (entry) {
          registerAliases(entry, candidates);
        }
      }

      if (!entry) {
        return;
      }

      if (!entry.fingerLabel && candidates[0]) {
        entry.fingerLabel = candidates[0];
      }
      if (
        (entry.finger === null || entry.finger === undefined) &&
        block.finger_number !== null &&
        block.finger_number !== undefined
      ) {
        entry.finger = Math.trunc(block.finger_number);
      }

      entry.blocks.push(block);
    });

    const uniqueEntries = Array.from(new Set(map.values()));

    const result = uniqueEntries
      .map((entry) => {
        const fingerInfo = entry.fingerInfo;
        const fingerLabel = hasValue(fingerInfo?.finger_label)
          ? String(fingerInfo.finger_label).trim()
          : entry.fingerLabel;
        const fingerNumberCandidate = Number.isFinite(entry.finger)
          ? entry.finger
          : Number.isFinite(fingerInfo?.finger_number)
          ? fingerInfo.finger_number
          : parseNumeric(fingerLabel);
        const fingerNumber = Number.isFinite(fingerNumberCandidate)
          ? Math.trunc(fingerNumberCandidate)
          : null;

        entry.blocks.sort((a, b) => {
          const updatedA = a.latest_update_value ?? 0;
          const updatedB = b.latest_update_value ?? 0;
          if (updatedA !== updatedB) {
            return updatedA - updatedB;
          }
          return a.block_id.localeCompare(b.block_id, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        });

        const fillSource = Number.isFinite(fingerInfo?.percentage_fill_value)
          ? fingerInfo.percentage_fill_value
          : parseNumeric(fingerInfo?.percentage_fill);
        const fill = Number.isFinite(fillSource) ? fillSource : null;

        let hoursSum = 0;
        let hoursCount = 0;
        entry.blocks.forEach((block) => {
          if (isDepletedStatus(block.block_status)) {
            return;
          }

          const hoursValue = parseNumeric(block.hours_remaining);
          if (Number.isFinite(hoursValue)) {
            hoursSum += hoursValue;
            hoursCount += 1;
          }
        });
        const hoursRemainingTotal = hoursCount > 0 ? hoursSum : null;

        const key = hasValue(fingerInfo?.finger_key)
          ? String(fingerInfo.finger_key).trim()
          : entry.key || fingerLabel || (fingerNumber !== null ? String(fingerNumber) : null);

        if (!key) {
          return null;
        }

        return {
          key,
          orderKey: key,
          finger: fingerNumber,
          fingerLabel: fingerLabel || (fingerNumber !== null ? String(fingerNumber) : key),
          status: fingerInfo?.finger_status || "Active",
          fill,
          info: fingerInfo || null,
          hoursRemainingTotal,
          blocks: entry.blocks,
        };
      })
      .filter(Boolean);

    const orderCandidates = new Map();
    result.forEach((finger) => {
      const canonical = getFingerOrderKey(finger);
      const normalised = normaliseKeyText(canonical);
      if (normalised) {
        orderCandidates.set(normalised, canonical);
      }
    });

    const resolvedOrder = [];
    const seenOrder = new Set();

    fingerOrder.forEach((value) => {
      const normalised = normaliseKeyText(value);
      if (!normalised) {
        return;
      }
      if (!orderCandidates.has(normalised)) {
        return;
      }
      if (seenOrder.has(normalised)) {
        return;
      }
      seenOrder.add(normalised);
      resolvedOrder.push(orderCandidates.get(normalised));
    });

    result.forEach((finger) => {
      const canonical = getFingerOrderKey(finger);
      const normalised = normaliseKeyText(canonical);
      if (!normalised || seenOrder.has(normalised)) {
        return;
      }
      seenOrder.add(normalised);
      resolvedOrder.push(canonical);
    });

    const lookup = new Map();
    resolvedOrder.forEach((value, index) => {
      const normalised = normaliseKeyText(value);
      if (normalised && !lookup.has(normalised)) {
        lookup.set(normalised, index);
      }
    });

    const normalisedQuery = normaliseText(searchQuery);
    const selectedStatusSet = new Set(statusFilters);
    const fromTimestamp = toDateValue(dateFrom || null);
    const toTimestamp = toDateValue(dateTo || null);

    const filterByStatus = (finger) => {
      const normalisedStatus = normaliseText(finger.status);

      let statusValue = "other";
      if (normalisedStatus.includes("deplet")) {
        statusValue = "depleted";
      } else if (statusOptionValues.has(normalisedStatus)) {
        statusValue = normalisedStatus;
      }

      if (!selectedStatusSet.size) {
        return false;
      }

      return selectedStatusSet.has(statusValue);
    };

    const matchesSearch = (block, finger) => {
      if (!normalisedQuery) {
        return true;
      }

      const fingerLabel = normaliseText(finger.fingerLabel);
      if (fingerLabel.includes(normalisedQuery)) {
        return true;
      }

      const candidates = [
        block.block_id,
        block.block_status,
        block.product_grade,
        block.product_classification,
        block.ore_classification,
      ];

      return candidates.some((value) => normaliseText(value).includes(normalisedQuery));
    };

    const matchesTimeline = (block) => {
      if (!fromTimestamp && !toTimestamp) {
        return true;
      }

      const timestamp = getBlockRelevantTimestamp(block);
      if (timestamp === null) {
        return false;
      }

      if (fromTimestamp && timestamp < fromTimestamp) {
        return false;
      }

      if (toTimestamp && timestamp > toTimestamp) {
        return false;
      }

      return true;
    };

    const matchesDepletedFilter = (block) => {
      if (includeDepletedBlocks) {
        return true;
      }

      return !isDepletedStatus(block.block_status);
    };

    const filtered = result
      .filter((finger) => filterByStatus(finger))
      .map((finger) => {
        const filteredBlocks = finger.blocks.filter(
          (block) =>
            matchesSearch(block, finger) &&
            matchesTimeline(block) &&
            matchesDepletedFilter(block)
        );

        if (!filteredBlocks.length) {
          return null;
        }

        let hoursSum = 0;
        let hoursCount = 0;
        filteredBlocks.forEach((block) => {
          const hoursValue = parseNumeric(block.hours_remaining);
          if (Number.isFinite(hoursValue)) {
            hoursSum += hoursValue;
            hoursCount += 1;
          }
        });

        const hoursRemainingTotal = hoursCount > 0 ? hoursSum : null;

        return {
          ...finger,
          blocks: filteredBlocks,
          hoursRemainingTotal,
        };
      })
      .filter(Boolean);

    const sorted = filtered
      .slice()
      .sort((a, b) => {
        const keyA = normaliseKeyText(getFingerOrderKey(a));
        const keyB = normaliseKeyText(getFingerOrderKey(b));
        const indexA = keyA !== null && lookup.has(keyA) ? lookup.get(keyA) : Number.POSITIVE_INFINITY;
        const indexB = keyB !== null && lookup.has(keyB) ? lookup.get(keyB) : Number.POSITIVE_INFINITY;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const numberA = parseNumeric(a.finger ?? a.fingerLabel ?? getFingerOrderKey(a));
        const numberB = parseNumeric(b.finger ?? b.fingerLabel ?? getFingerOrderKey(b));

        if (Number.isFinite(numberA) && Number.isFinite(numberB) && numberA !== numberB) {
          return numberA - numberB;
        }

        const labelA = a.fingerLabel || getFingerOrderKey(a) || "";
        const labelB = b.fingerLabel || getFingerOrderKey(b) || "";

        return labelA.localeCompare(labelB, undefined, { numeric: true, sensitivity: "base" });
      });

    return {
      groupedFingers: sorted.slice(0, 28),
      resolvedFingerOrder: resolvedOrder,
      fingerOrderLookup: lookup,
    };
  }, [
    blocks,
    fingers,
    fingerOrder,
    searchQuery,
    statusFilters,
    dateFrom,
    dateTo,
    statusOptionValues,
    includeDepletedBlocks,
  ]);

  useEffect(() => {
    setFingerOrder((current) => {
      if (arraysEqual(current, resolvedFingerOrder)) {
        return current;
      }

      return resolvedFingerOrder;
    });
  }, [resolvedFingerOrder]);

  const fingerOrderOptions = useMemo(
    () => resolvedFingerOrder.map((_, index) => index + 1),
    [resolvedFingerOrder]
  );

  const fingerOrderUpdatedLabel = useMemo(() => {
    if (!hasValue(fingerOrderUpdatedAt)) {
      return null;
    }

    return formatDate(fingerOrderUpdatedAt);
  }, [fingerOrderUpdatedAt]);

  useEffect(() => {
    if (!fingerOrderUpdatedLabel) {
      setShowFingerOrderUpdatedMeta(false);
      return undefined;
    }

    setShowFingerOrderUpdatedMeta(true);
    const timeout = window.setTimeout(() => {
      setShowFingerOrderUpdatedMeta(false);
    }, 3000);

    return () => window.clearTimeout(timeout);
  }, [fingerOrderUpdatedLabel]);

  const totalFingerHours = useMemo(() => {
    if (!groupedFingers.length) {
      return null;
    }

    let total = 0;
    let hasValue = false;

    groupedFingers.forEach((finger) => {
      const value = parseNumeric(finger.hoursRemainingTotal);
      if (value === null || !Number.isFinite(value)) {
        return;
      }

      total += value;
      hasValue = true;
    });

    if (!hasValue) {
      return null;
    }

    return total;
  }, [groupedFingers]);

  const totalFingerHoursLabel = useMemo(() => {
    if (totalFingerHours === null) {
      return "—";
    }

    return formatDurationSummary(totalFingerHours);
  }, [totalFingerHours]);

  const selectedBlock = useMemo(
    () => blocks.find((block) => block.block_id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );

  useEffect(() => {
    if (!selectedFingerKey) {
      return;
    }

    const exists = groupedFingers.some((finger) => finger.key === selectedFingerKey);
    if (!exists) {
      setSelectedFingerKey(null);
    }
  }, [groupedFingers, selectedFingerKey]);

  const selectedFinger = useMemo(
    () => groupedFingers.find((finger) => finger.key === selectedFingerKey) || null,
    [groupedFingers, selectedFingerKey]
  );

  const sanitiseFingerOrderState = useCallback(
    (order) => {
      if (!resolvedFingerOrder.length) {
        return [];
      }

      const canonicalMap = new Map();
      resolvedFingerOrder.forEach((value) => {
        const normalised = normaliseKeyText(value);
        if (normalised && !canonicalMap.has(normalised)) {
          canonicalMap.set(normalised, value);
        }
      });

      const seen = new Set();
      const result = [];

      (Array.isArray(order) ? order : []).forEach((value) => {
        const normalised = normaliseKeyText(value);
        if (!normalised) {
          return;
        }

        if (!canonicalMap.has(normalised) || seen.has(normalised)) {
          return;
        }

        seen.add(normalised);
        result.push(canonicalMap.get(normalised));
      });

      canonicalMap.forEach((value, normalised) => {
        if (!seen.has(normalised)) {
          seen.add(normalised);
          result.push(value);
        }
      });

      return result;
    },
    [resolvedFingerOrder]
  );

  const commitFingerOrder = useCallback(
    async (nextOrder) => {
      const prepared = sanitiseFingerOrderState(nextOrder);

      if (arraysEqual(prepared, resolvedFingerOrder)) {
        return true;
      }

      const previousOrder = resolvedFingerOrder;
      const previousUpdatedAt = fingerOrderUpdatedAt;
      setFingerOrder(prepared);

      const requestId = fingerOrderRequestIdRef.current + 1;
      fingerOrderRequestIdRef.current = requestId;
      setFingerOrderSaving(true);
      setFingerOrderFeedback(null);

      try {
        const response = await fetch("/api/index.php?resource=finger-order", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ order: prepared }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        if (!payload || payload.status !== "ok" || !Array.isArray(payload?.data?.order)) {
          throw new Error(payload?.message || "Unexpected API response.");
        }

        const savedOrder = sanitiseFingerOrderState(sanitiseFingerOrderInput(payload.data.order));
        const savedUpdatedAt = hasValue(payload?.data?.updated_at)
          ? String(payload.data.updated_at).trim()
          : null;
        if (fingerOrderRequestIdRef.current === requestId) {
          setFingerOrder(savedOrder);
          setFingerOrderUpdatedAt(savedUpdatedAt || null);
          setFingerOrderFeedback({ type: "success", message: "Finger order updated." });
        }

        return true;
      } catch (commitError) {
        if (fingerOrderRequestIdRef.current === requestId) {
          setFingerOrder(previousOrder);
          setFingerOrderUpdatedAt(previousUpdatedAt || null);
          const message = commitError?.message
            ? String(commitError.message)
            : "Unable to update finger order.";
          setFingerOrderFeedback({ type: "error", message });
        }

        return false;
      } finally {
        if (fingerOrderRequestIdRef.current === requestId) {
          setFingerOrderSaving(false);
        }
      }
    },
    [resolvedFingerOrder, sanitiseFingerOrderState, fingerOrderUpdatedAt]
  );

  useEffect(() => {
    return () => {
      if (fingerOrderFeedbackTimerRef.current) {
        clearTimeout(fingerOrderFeedbackTimerRef.current);
        fingerOrderFeedbackTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (fingerOrderFeedbackTimerRef.current) {
      clearTimeout(fingerOrderFeedbackTimerRef.current);
      fingerOrderFeedbackTimerRef.current = null;
    }

    if (!fingerOrderFeedback) {
      return undefined;
    }

    const duration = fingerOrderFeedback.type === "error" ? 6000 : 3200;
    const timeout = setTimeout(() => {
      setFingerOrderFeedback(null);
      fingerOrderFeedbackTimerRef.current = null;
    }, duration);

    fingerOrderFeedbackTimerRef.current = timeout;

    return () => {
      clearTimeout(timeout);
      if (fingerOrderFeedbackTimerRef.current === timeout) {
        fingerOrderFeedbackTimerRef.current = null;
      }
    };
  }, [fingerOrderFeedback]);

  useEffect(() => {
    if (selectedBlock) {
      setActiveBlock(selectedBlock);
      const frame = requestAnimationFrame(() => setIsModalOpen(true));
      return () => cancelAnimationFrame(frame);
    }

    setIsModalOpen(false);
    return undefined;
  }, [selectedBlock]);

  useEffect(() => {
    if (selectedFinger) {
      setActiveFinger(selectedFinger);
      const frame = requestAnimationFrame(() => setIsFingerModalOpen(true));
      return () => cancelAnimationFrame(frame);
    }

    setIsFingerModalOpen(false);
    return undefined;
  }, [selectedFinger]);

  useEffect(() => {
    if (!activeFinger) {
      setFingerFillInitial(null);
      setFingerFillDraft(null);
      setFingerFillFeedback(null);
      return;
    }

    const resolvedInitial = clampPercentageValue(activeFinger.fill);
    const initialValue = resolvedInitial === null ? 0 : resolvedInitial;

    setFingerFillInitial(initialValue);
    setFingerFillDraft(initialValue);
    setFingerFillFeedback(null);
  }, [activeFinger]);

  useEffect(() => {
    if (!isModalOpen) {
      if (!activeBlock) {
        return undefined;
      }

      const timeout = setTimeout(() => {
        setActiveBlock(null);
      }, modalTransitionDuration);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [isModalOpen, activeBlock, modalTransitionDuration]);

  useEffect(() => {
    if (!isFingerModalOpen) {
      if (!activeFinger) {
        return undefined;
      }

      const timeout = setTimeout(() => {
        setActiveFinger(null);
      }, modalTransitionDuration);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [isFingerModalOpen, activeFinger, modalTransitionDuration]);

  const handleCloseDetail = useCallback(() => {
    setSelectedBlockId(null);
  }, []);

  const handleCloseFingerDetail = useCallback(() => {
    setSelectedFingerKey(null);
  }, []);

  const handleBlockSelect = useCallback((blockId) => {
    if (!blockId) {
      return;
    }

    setSelectedBlockId(blockId);
    setSelectedFingerKey(null);
    setIsLegendOpen(false);
  }, []);

  const handleFingerDragStart = useCallback((event, finger) => {
    const key = getFingerOrderKey(finger);
    if (!key) {
      return;
    }

    try {
      event.dataTransfer.setData("text/plain", key);
    } catch (setDataError) {
      // Ignore browsers that prevent setting data.
    }
    event.dataTransfer.effectAllowed = "move";
    setDraggingFingerKey(key);
    setFingerDropIndicator(null);
  }, []);

  const handleFingerDragEnd = useCallback(() => {
    setDraggingFingerKey(null);
    setFingerDropIndicator(null);
  }, []);

  const handleFingerDragOver = useCallback(
    (event, targetKey) => {
      if (!draggingFingerKey || draggingFingerKey === targetKey) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      const rect = event.currentTarget.getBoundingClientRect();
      const shouldPlaceAfter = event.clientY >= rect.top + rect.height / 2;
      const position = shouldPlaceAfter ? "after" : "before";
      setFingerDropIndicator((current) => {
        if (current && current.key === targetKey && current.position === position) {
          return current;
        }
        return { key: targetKey, position };
      });
    },
    [draggingFingerKey]
  );

  const handleFingerDragLeave = useCallback(
    (event, targetKey) => {
      if (!fingerDropIndicator || fingerDropIndicator.key !== targetKey) {
        return;
      }

      if (event.currentTarget.contains(event.relatedTarget)) {
        return;
      }

      setFingerDropIndicator(null);
    },
    [fingerDropIndicator]
  );

  const handleFingerDrop = useCallback(
    async (event, targetKey) => {
      if (!draggingFingerKey) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const shouldPlaceAfter = event.clientY >= rect.top + rect.height / 2;
      setFingerDropIndicator(null);
      setDraggingFingerKey(null);

      if (draggingFingerKey === targetKey) {
        return;
      }

      const nextOrder = reorderFingerOrder(
        resolvedFingerOrder,
        draggingFingerKey,
        targetKey,
        shouldPlaceAfter ? "after" : "before"
      );

      if (arraysEqual(nextOrder, resolvedFingerOrder)) {
        return;
      }

      await commitFingerOrder(nextOrder);
    },
    [draggingFingerKey, resolvedFingerOrder, commitFingerOrder]
  );

  const handleFingerGridDragOver = useCallback(
    (event) => {
      if (!draggingFingerKey || event.target !== event.currentTarget) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setFingerDropIndicator(null);
    },
    [draggingFingerKey]
  );

  const handleFingerGridDrop = useCallback(
    async (event) => {
      if (!draggingFingerKey || event.target !== event.currentTarget) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setFingerDropIndicator(null);
      const nextOrder = resolvedFingerOrder.filter((value) => value !== draggingFingerKey);
      nextOrder.push(draggingFingerKey);
      setDraggingFingerKey(null);

      if (arraysEqual(nextOrder, resolvedFingerOrder)) {
        return;
      }

      await commitFingerOrder(nextOrder);
    },
    [draggingFingerKey, resolvedFingerOrder, commitFingerOrder]
  );

  const handleFingerSelect = useCallback((finger) => {
    if (!finger) {
      return;
    }

    const keyCandidate = [
      finger.key,
      finger.info?.finger_key,
      finger.fingerLabel,
      Number.isFinite(finger.finger) ? String(Math.trunc(finger.finger)) : null,
    ]
      .map((value) => {
        if (value === null || value === undefined) {
          return null;
        }
        const text = `${value}`.trim();
        return text === "" ? null : text;
      })
      .find((value) => value);

    if (!keyCandidate) {
      return;
    }

    setSelectedFingerKey(keyCandidate);
    setSelectedBlockId(null);
    setIsLegendOpen(false);
  }, []);

  const handleFingerOrderSelectChange = useCallback(
    async (finger, position) => {
      const key = getFingerOrderKey(finger);
      if (!key) {
        return;
      }

      const index = clampIndex(position, 0, Math.max(resolvedFingerOrder.length - 1, 0));
      const nextOrder = resolvedFingerOrder.filter((value) => value !== key);
      nextOrder.splice(index, 0, key);

      if (arraysEqual(nextOrder, resolvedFingerOrder)) {
        return;
      }

      await commitFingerOrder(nextOrder);
    },
    [resolvedFingerOrder, commitFingerOrder]
  );

  const handleFingerFillChange = useCallback((event) => {
    const { value } = event.target;
    const nextValue = clampPercentageValue(value);
    if (nextValue === null) {
      return;
    }

    setFingerFillDraft(nextValue);
    setFingerFillFeedback(null);
  }, []);

  const handleFingerFillReset = useCallback(() => {
    if (!Number.isFinite(fingerFillInitial)) {
      return;
    }

    setFingerFillDraft(fingerFillInitial);
    setFingerFillFeedback(null);
  }, [fingerFillInitial]);

  const handleFingerFillSave = useCallback(async () => {
    if (!activeFinger) {
      return;
    }

    const identifier = hasValue(activeFinger.info?.finger_nr)
      ? String(activeFinger.info.finger_nr).trim()
      : activeFinger.fingerLabel || activeFinger.key;
    const nextValue = clampPercentageValue(fingerFillDraft);

    if (!hasValue(identifier) || nextValue === null) {
      setFingerFillFeedback({
        type: "error",
        message: "A valid finger and fill percentage are required.",
      });
      return;
    }

    try {
      setFingerFillSaving(true);
      setFingerFillFeedback(null);

      const response = await fetch("/api/index.php?resource=finger-fill", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          finger: identifier,
          fill: nextValue,
        }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload || payload.status !== "ok") {
        const message = payload?.message || `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      await refreshRomPadData();

      setFingerFillInitial(nextValue);
      setFingerFillDraft(nextValue);
      setFingerFillFeedback({
        type: "success",
        message: "Fill level updated successfully.",
      });
    } catch (saveError) {
      if (saveError.name === "AbortError") {
        return;
      }

      setFingerFillFeedback({
        type: "error",
        message: saveError.message || "Unable to update the fill level.",
      });
    } finally {
      setFingerFillSaving(false);
    }
  }, [activeFinger, fingerFillDraft, refreshRomPadData]);

  useEffect(() => {
    if (!isModalOpen && !isFingerModalOpen && !isLegendOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (isModalOpen) {
          handleCloseDetail();
        } else if (isFingerModalOpen) {
          handleCloseFingerDetail();
        } else if (isLegendOpen) {
          handleCloseLegend();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      style.overflow = previousOverflow;
    };
  }, [
    handleCloseDetail,
    handleCloseFingerDetail,
    handleCloseLegend,
    isFingerModalOpen,
    isLegendOpen,
    isModalOpen,
  ]);

  const renderBlockIndicators = (block, { containerClassName = "" } = {}) => {
    const indicators = INDICATOR_CONFIG.filter(
      (indicator) => indicator.displayAsCircle !== false
    )
      .map((indicator) => {
        const value = block[indicator.key];
        const shouldDisplay = indicator.shouldDisplay
          ? indicator.shouldDisplay(value, block)
          : hasValue(value);

        if (!shouldDisplay) {
          return null;
        }

        const color = resolveIndicatorColor(indicator, block);
        const formattedValue = indicator.formatValue
          ? indicator.formatValue(value, block)
          : formatText(value);

        return (
          <span
            key={indicator.key}
            className="rom-pad__indicator"
            style={{ "--indicator-color": color }}
            role="listitem"
            aria-label={`${indicator.label}: ${formattedValue}`}
            title={`${indicator.label}: ${formattedValue}`}
          />
        );
      })
      .filter(Boolean);

    if (!indicators.length) {
      return null;
    }

    const className = ["rom-pad__block-indicators", containerClassName]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={className}
        role="list"
        aria-label={`Status indicators for block ${block.block_id || "unknown"}`}
      >
        {indicators}
      </div>
    );
  };

  const renderIndicatorPill = (
    block,
    key,
    { className = "", showLabel = false, emptyLabel = "—" } = {}
  ) => {
    const indicator = INDICATOR_LOOKUP.get(key);
    if (!indicator) {
      const styles = getPillVisualStyles(NEUTRAL_PILL_COLOR);

      return (
        <span
          className={`rom-pad__pill ${className}`.trim()}
          style={{
            "--pill-color": styles.background,
            "--pill-border-color": styles.border,
            "--pill-text-color": styles.text,
          }}
        >
          {emptyLabel}
        </span>
      );
    }

    const value = block ? block[indicator.key] : null;
    const hasData = indicator.shouldDisplay
      ? indicator.shouldDisplay(value, block)
      : hasValue(value);
    const formattedValue = indicator.formatValue
      ? indicator.formatValue(value, block)
      : formatText(value);
    const displayValue = hasData ? formattedValue : emptyLabel;
    const resolvedColor = hasData
      ? resolveIndicatorColor(indicator, block)
      : NEUTRAL_PILL_COLOR;
    const styles = getPillVisualStyles(resolvedColor, {
      backgroundAlpha: 0.12,
      borderAlpha: 0.32,
    });
    const pillClassName = [
      "rom-pad__pill",
      className,
      showLabel ? "rom-pad__pill--stacked" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <span
        className={pillClassName}
        style={{
          "--pill-color": styles.background,
          "--pill-border-color": styles.border,
          "--pill-text-color": styles.text,
        }}
      >
        {showLabel ? (
          <>
            <span className="rom-pad__pill-label">{indicator.label}</span>
            <span className="rom-pad__pill-value">{displayValue}</span>
          </>
        ) : (
          <span className="rom-pad__pill-value">{displayValue}</span>
        )}
      </span>
    );
  };

  const renderBlockIdPill = (block) => {
    const hasCoreData = blockHasCoreData(block);
    const color = hasCoreData ? "#1565c0" : "#607d8b";
    const styles = getPillVisualStyles(color, {
      backgroundAlpha: 0.18,
      borderAlpha: 0.45,
    });

    return (
      <span
        className="rom-pad__pill rom-pad__pill--block"
        style={{
          "--pill-color": styles.background,
          "--pill-border-color": styles.border,
          "--pill-text-color": styles.text,
        }}
      >
        <span className="rom-pad__pill-icon" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="1.5" y="1.5" width="13" height="13" rx="3" stroke="currentColor" />
            <path
              d="M5.25 3.75H10.75M5.25 6.75H10.75M5.25 9.75H8.75"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span className="rom-pad__pill-value">{block.block_id || "—"}</span>
      </span>
    );
  };

  const fingerByBlockId = useMemo(() => {
    const map = new Map();
    groupedFingers.forEach((finger) => {
      finger.blocks.forEach((block) => {
        map.set(block.block_id, finger);
      });
    });
    return map;
  }, [groupedFingers]);

  const renderBlockContent = (block) => {
    const indicatorRow = renderBlockIndicators(block, {
      containerClassName: "rom-pad__block-row rom-pad__block-row--indicators",
    });

    return (
      <div className="rom-pad__block-content">
        <div className="rom-pad__block-meta">
          <div className="rom-pad__block-row rom-pad__block-row--id">
            {renderBlockIdPill(block)}
          </div>
          <div className="rom-pad__block-row rom-pad__block-row--metrics">
            {renderIndicatorPill(block, "p_grade", {
              className: "rom-pad__pill--metric",
              showLabel: false,
            })}
            {renderIndicatorPill(block, "hours_remaining", {
              className: "rom-pad__pill--metric",
              showLabel: false,
            })}
          </div>
          {indicatorRow}
        </div>
      </div>
    );
  };

  const renderTooltip = (block) => {
    return (
      <div className="rom-pad__block-tooltip" role="tooltip">
        <div>
          <span>Status</span>
          <strong>{formatText(block.block_status)}</strong>
        </div>
        <div>
          <span>P-grade</span>
          <strong>{formatNumber(block.p_grade, { maximumFractionDigits: 2 })}</strong>
        </div>
        <div>
          <span>Moisture</span>
          <strong>{formatText(block.moisture)}</strong>
        </div>
        <div>
          <span>Product classification</span>
          <strong>{formatText(block.product_classification)}</strong>
        </div>
      </div>
    );
  };

  const activeBlockFinger = activeBlock ? fingerByBlockId.get(activeBlock.block_id) || null : null;

  const fingerModalData = useMemo(() => {
    if (!activeFinger) {
      return null;
    }

    const label = activeFinger.fingerLabel || activeFinger.key || "—";
    const info = activeFinger.info || {};
    const statusSource = hasValue(info.finger_status)
      ? info.finger_status
      : activeFinger.status;
    const resolvedStatus = hasValue(statusSource) ? statusSource : "Active";
    const statusLabel = formatText(resolvedStatus);
    const statusKey = getFingerStatusKey(resolvedStatus);
    const fillValue = Number.isFinite(fingerFillDraft)
      ? fingerFillDraft
      : activeFinger.fill;
    const fill = formatPercentage(fillValue);
    const blocksCount = activeFinger.blocks.length;
    const latestUpdate = info.latest_update || null;
    const formatField = (value) => formatText(value);
    const performanceFeedback = formatField(info.performance_feedback);
    const blendingRatios = formatField(info.blending_ratios);
    const feedingSequence = formatField(info.feeding_sequence);
    const routingStatus = formatField(resolvedStatus);
    const shiftType = formatField(info.shift_type);
    const tailsAppearance = formatField(info.tails_appearance);
    const routingMoisture = formatField(info.moisture);
    const concrete = formatField(info.concrete);
    const floodObservation = formatField(info.flood_observation);
    const flowStatus = formatField(info.flow_status);
    const flatBottoms = formatField(info.flat_bottoms);
    const stackScreens = formatField(info.stack_screens);
    const wash1 = formatField(info.wash_1);
    const hoursTotalLabel =
      activeFinger.hoursRemainingTotal !== null
        ? formatHoursTotal(activeFinger.hoursRemainingTotal)
        : null;

    return {
      label,
      statusLabel,
      statusKey,
      fill,
      blocksCount,
      latestUpdate,
      performanceFeedback,
      blendingRatios,
      feedingSequence,
      routingStatus,
      shiftType,
      tailsAppearance,
      routingMoisture,
      concrete,
      floodObservation,
      flowStatus,
      flatBottoms,
      stackScreens,
      wash1,
      hoursTotalLabel,
    };
  }, [activeFinger, fingerFillDraft]);

  const activeFingerOrderSelectId = useMemo(() => {
    if (!activeFinger) {
      return "finger-order-select";
    }

    const key = getFingerOrderKey(activeFinger) || "finger-order";
    const normalised = normaliseKeyText(key) || key;
    return `finger-order-${normalised.replace(/[^a-z0-9_-]+/gi, "-")}`;
  }, [activeFinger]);

  const activeFingerOrderPosition = useMemo(() => {
    if (!activeFinger) {
      return null;
    }

    const key = getFingerOrderKey(activeFinger);
    const normalised = normaliseKeyText(key);
    if (!normalised) {
      return null;
    }

    if (fingerOrderLookup.has(normalised)) {
      return fingerOrderLookup.get(normalised) + 1;
    }

    const index = resolvedFingerOrder.findIndex((value) => normaliseKeyText(value) === normalised);
    return index >= 0 ? index + 1 : null;
  }, [activeFinger, fingerOrderLookup, resolvedFingerOrder]);

  const fingerHeaderStats = useMemo(() => {
    if (!fingerModalData) {
      return [];
    }

    const stats = [
      { label: "Fill level", value: fingerModalData.fill },
    ];

    if (fingerModalData.hoursTotalLabel) {
      stats.push({ label: "Hours remaining", value: fingerModalData.hoursTotalLabel });
    }

    stats.push({
      label: "Blocks assigned",
      value: String(fingerModalData.blocksCount),
    });

    return stats;
  }, [fingerModalData]);

  const plantRoutingFields = useMemo(() => {
    if (!fingerModalData) {
      return [];
    }

    return [
      { label: "Routing status", value: fingerModalData.routingStatus },
      { label: "Shift type", value: fingerModalData.shiftType },
      { label: "Performance feedback", value: fingerModalData.performanceFeedback },
      { label: "Blending ratios", value: fingerModalData.blendingRatios },
      { label: "Feeding sequence", value: fingerModalData.feedingSequence },
      { label: "Tails appearance", value: fingerModalData.tailsAppearance },
      { label: "Moisture", value: fingerModalData.routingMoisture },
      { label: "Concrete", value: fingerModalData.concrete },
      { label: "Flood observation", value: fingerModalData.floodObservation },
      { label: "Flow status", value: fingerModalData.flowStatus },
      { label: "Flat bottoms", value: fingerModalData.flatBottoms },
      { label: "Stack screens", value: fingerModalData.stackScreens },
      { label: "Wash 1", value: fingerModalData.wash1 },
    ];
  }, [fingerModalData]);

  const sliderValue = Number.isFinite(fingerFillDraft)
    ? fingerFillDraft
    : Number.isFinite(fingerFillInitial)
    ? fingerFillInitial
    : 0;
  const fingerFillLabel = activeFinger ? formatPercentage(sliderValue) : "—";
  const fingerFillDirty =
    Number.isFinite(fingerFillInitial) &&
    Number.isFinite(fingerFillDraft) &&
    Math.abs(fingerFillDraft - fingerFillInitial) > 0.09;
  const fingerFillProgressStyle = {
    "--finger-fill-progress": `${Math.min(Math.max(sliderValue, 0), 100)}%`,
  };

  const isStatusFilterDefault = useMemo(() => {
    if (statusFilters.length !== DEFAULT_STATUS_FILTER.length) {
      return false;
    }

    const defaultSet = new Set(DEFAULT_STATUS_FILTER);
    return statusFilters.every((value) => defaultSet.has(value));
  }, [statusFilters]);

  const hasTimelineFilter = Boolean(dateFrom || dateTo);
  const hasActiveFilter =
    !isStatusFilterDefault || hasTimelineFilter || includeDepletedBlocks;

  const handleStatusToggle = (value) => {
    setStatusFilters((previous) => {
      if (previous.includes(value)) {
        return previous.filter((item) => item !== value);
      }
      return [...previous, value];
    });
  };

  const handleResetFilters = () => {
    setStatusFilters(DEFAULT_STATUS_FILTER);
    setDateFrom("");
    setDateTo("");
    setIncludeDepletedBlocks(false);
  };

  return (
    <div className={`rom-pad ${isMounted ? "rom-pad--mounted" : ""}`}>
      <div className="rom-pad__layout">
        <section className="rom-pad__content" aria-label="ROM pad overview">
          <header className="rom-pad__header">
            <div
              className="rom-pad__summary"
              role="status"
              aria-live="polite"
              aria-label="Combined finger availability"
            >
              <span className="rom-pad__summary-label">Time to Depletion</span>
              <span className="rom-pad__summary-value">{totalFingerHoursLabel}</span>
            </div>
            <div className="rom-pad__filters" aria-label="Filters">
              <div className="rom-pad__search">
                <span className="rom-pad__search-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M15.5 15.5L20 20"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="11"
                      cy="11"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                </span>
                <input
                  type="search"
                  placeholder="Search blocks…"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  aria-label="Search for blocks"
                />
              </div>
              <div className="rom-pad__filter-actions">
                <button
                  type="button"
                  className={`rom-pad__filter-toggle ${
                    hasActiveFilter ? "rom-pad__filter-toggle--active" : ""
                  }`}
                  onClick={() => {
                    setIsLegendOpen(false);
                    setIsFilterPanelOpen((open) => !open);
                  }}
                  aria-haspopup="true"
                  aria-expanded={isFilterPanelOpen}
                  aria-controls={filterPanelId}
                  ref={filterToggleRef}
                >
                  <span className="rom-pad__filter-toggle-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false">
                      <path
                        d="M4 6h16M7 12h10M10 18h4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </span>
                  <span>Filters</span>
                  {hasActiveFilter ? <span className="rom-pad__filter-indicator" /> : null}
                </button>
                {isFilterPanelOpen ? (
                  <div
                    className="rom-pad__filter-panel"
                    id={filterPanelId}
                    role="dialog"
                    aria-label="Filter ROM pad view"
                    ref={filterPanelRef}
                  >
                    <div className="rom-pad__filter-section">
                      <span className="rom-pad__filter-section-title">Finger status</span>
                      <div className="rom-pad__filter-options">
                        {FINGER_STATUS_OPTIONS.map((option) => {
                          const inputId = `${filterPanelId}-${option.value.replace(/\s+/g, "-")}`;
                          const checked = statusFilters.includes(option.value);
                          return (
                            <label key={option.value} htmlFor={inputId} className="rom-pad__filter-option">
                              <input
                                id={inputId}
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleStatusToggle(option.value)}
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div className="rom-pad__filter-section">
                      <span className="rom-pad__filter-section-title">Blocks</span>
                      <div className="rom-pad__filter-options">
                        <label
                          htmlFor={`${filterPanelId}-include-depleted-blocks`}
                          className="rom-pad__filter-option"
                        >
                          <input
                            id={`${filterPanelId}-include-depleted-blocks`}
                            type="checkbox"
                            checked={includeDepletedBlocks}
                            onChange={(event) => setIncludeDepletedBlocks(event.target.checked)}
                          />
                          <span>Depleted blocks</span>
                        </label>
                      </div>
                    </div>
                    <div className="rom-pad__filter-section">
                      <span className="rom-pad__filter-section-title">Timeline</span>
                      <div className="rom-pad__filter-dates">
                        <label>
                          <span>From</span>
                          <input
                            type="datetime-local"
                            value={dateFrom}
                            onChange={(event) => {
                              const { value } = event.target;
                              setDateFrom(value);
                              setDateTo((previous) => {
                                if (!previous || !value) {
                                  return previous;
                                }
                                return previous < value ? value : previous;
                              });
                            }}
                          />
                        </label>
                        <label>
                          <span>To</span>
                          <input
                            type="datetime-local"
                            value={dateTo}
                            onChange={(event) => {
                              const { value } = event.target;
                              if (dateFrom && value && value < dateFrom) {
                                setDateTo(dateFrom);
                                return;
                              }
                              setDateTo(value);
                            }}
                            min={dateFrom || undefined}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="rom-pad__filter-footer">
                      <button type="button" className="rom-pad__ghost-button" onClick={handleResetFilters}>
                        Reset filters
                      </button>
                    </div>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="rom-pad__legend-button"
                  onClick={handleOpenLegend}
                  aria-haspopup="dialog"
                  aria-expanded={isLegendOpen}
                  aria-controls={isLegendOpen ? legendDialogId : undefined}
                  ref={legendButtonRef}
                >
                  <span className="rom-pad__legend-button-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false">
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <path
                        d="M12 16v-3.6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <circle cx="12" cy="8.2" r="1.1" fill="currentColor" />
                    </svg>
                  </span>
                  <span>Legend?</span>
                </button>
              </div>
            </div>
          </header>

          {loading && (
            <div className="rom-pad__state" role="status">
              Loading ROM pad data…
            </div>
          )}

          {!loading && error && (
            <div className="rom-pad__state rom-pad__state--error" role="alert">
              <strong>Unable to load ROM pad data.</strong>
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && !groupedFingers.length && (
            <div className="rom-pad__state rom-pad__state--empty" role="status">
              No ROM pad blocks available.
            </div>
          )}

          {(fingerOrderSaving || fingerOrderFeedback) && (
            <div
              className={[
                "rom-pad__finger-order-feedback",
                fingerOrderFeedback ? `rom-pad__finger-order-feedback--${fingerOrderFeedback.type}` : null,
              ]
                .filter(Boolean)
                .join(" ")}
              role={fingerOrderFeedback?.type === "error" ? "alert" : "status"}
              aria-live="polite"
            >
              {fingerOrderSaving ? (
                <span className="rom-pad__finger-order-spinner" aria-hidden="true" />
              ) : fingerOrderFeedback?.type === "error" ? (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <path d="M12 7.5v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M20.25 7.75L10.5 17.5l-4.75-4.75"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              )}
              <span>
                {fingerOrderSaving
                  ? "Saving finger order…"
                  : fingerOrderFeedback?.message || ""}
              </span>
            </div>
          )}

          {!fingerOrderSaving &&
            !fingerOrderFeedback &&
            showFingerOrderUpdatedMeta &&
            fingerOrderUpdatedLabel && (
            <div className="rom-pad__finger-order-meta" role="status" aria-live="polite">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" fill="none" />
                <path d="M12 7.5v4.5l3 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              <span>Finger order last updated {fingerOrderUpdatedLabel}.</span>
            </div>
          )}

          <div
            className="rom-pad__grid"
            role="list"
            onDragOver={handleFingerGridDragOver}
            onDrop={handleFingerGridDrop}
          >
            {groupedFingers.map((finger) => {
              const fingerLabel = finger.fingerLabel || finger.key || "—";
              const fingerStatusLabel = finger.status || "Active";
              const fingerStatusKey = getFingerStatusKey(finger.status);
              const fillValue = Number.isFinite(finger.fill)
                ? Math.min(Math.max(finger.fill, 0), 100)
                : null;
              const fingerStyle = fillValue !== null ? { "--finger-fill": `${fillValue}%` } : {};
              const fingerHoursLabel =
                finger.hoursRemainingTotal !== null
                  ? formatHoursTotal(finger.hoursRemainingTotal)
                  : null;
              const fingerKey = getFingerOrderKey(finger);
              const isDragging = fingerKey && draggingFingerKey === fingerKey;
              const dropPosition =
                fingerDropIndicator && fingerDropIndicator.key === fingerKey
                  ? fingerDropIndicator.position
                  : null;
              const orderIndex = fingerKey
                ? resolvedFingerOrder.findIndex((value) => value === fingerKey)
                : -1;
              const orderDisplay = orderIndex >= 0 ? orderIndex + 1 : null;
              const fingerClassName = [
                "rom-pad__finger",
                isDragging ? "rom-pad__finger--dragging" : null,
                dropPosition ? `rom-pad__finger--drop-${dropPosition}` : null,
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <article
                  className={fingerClassName}
                  key={finger.key || fingerLabel}
                  role="listitem"
                  style={fingerStyle}
                  onDragEnter={fingerKey ? (event) => handleFingerDragOver(event, fingerKey) : undefined}
                  onDragOver={fingerKey ? (event) => handleFingerDragOver(event, fingerKey) : undefined}
                  onDragLeave={fingerKey ? (event) => handleFingerDragLeave(event, fingerKey) : undefined}
                  onDrop={fingerKey ? (event) => handleFingerDrop(event, fingerKey) : undefined}
                >
                  <header className="rom-pad__finger-header">
                    <button
                      type="button"
                      className="rom-pad__finger-drag-handle"
                      draggable
                      onDragStart={(event) => handleFingerDragStart(event, finger)}
                      onDragEnd={handleFingerDragEnd}
                      aria-label={`Reorder finger ${fingerLabel ? `F${fingerLabel}` : ""}`.trim()}
                    >
                      <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                        <path
                          d="M6 4.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Zm0 6a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Zm0 6a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Zm8-12a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Zm0 6a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Zm0 6a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="rom-pad__finger-header-button"
                      onClick={() => handleFingerSelect(finger)}
                      aria-label={`View details for finger ${fingerLabel}${orderDisplay ? `, position ${orderDisplay}` : ""}`}
                    >
                      <div className="rom-pad__finger-header-main">
                        <div className="rom-pad__finger-title-row">
                          <div className="rom-pad__finger-title">F{fingerLabel}</div>
                        </div>
                        {fingerHoursLabel ? (
                          <span className="rom-pad__finger-hours">{fingerHoursLabel}</span>
                        ) : null}
                      </div>
                      <span
                        className={`rom-pad__finger-status rom-pad__finger-status--${fingerStatusKey}`}
                      >
                        {fingerStatusLabel}
                      </span>
                    </button>
                  </header>

                  <div className="rom-pad__slider" aria-label={`Finger ${fingerLabel} utilisation`}>
                    <div className="rom-pad__track">
                      {finger.blocks.map((block) => {
                        const isSelected = selectedBlockId === block.block_id;
                        const blockStatusKey = getBlockStatusKey(block.block_status);
                        const oreClassificationKey = getOreClassificationKey(block);
                        const isBlockDepleted = blockStatusKey === "depleted";
                        const blockClassName = [
                          "rom-pad__block",
                          isSelected ? "rom-pad__block--selected" : null,
                          blockStatusKey ? `rom-pad__block--status-${blockStatusKey}` : null,
                          !isBlockDepleted && oreClassificationKey
                            ? `rom-pad__block--ore-${oreClassificationKey}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        const tonnageLabel = formatNumber(block.tonnage, {
                          maximumFractionDigits: 0,
                        });
                        const ariaParts = [
                          block.block_id ? `Block ${block.block_id}` : null,
                          fingerLabel ? `finger ${fingerLabel}` : null,
                          tonnageLabel !== "—" ? `${tonnageLabel} tonnes` : null,
                          hasValue(block.block_status)
                            ? `status ${formatText(block.block_status)}`
                            : null,
                          hasValue(block.product_grade)
                            ? `grade ${formatText(block.product_grade)}`
                            : null,
                        ].filter(Boolean);

                        return (
                          <button
                            key={block.block_id}
                            type="button"
                            className={blockClassName}
                            onClick={() => handleBlockSelect(block.block_id)}
                            aria-label={ariaParts.join(", ")}
                            aria-pressed={isSelected}
                          >
                            {renderBlockContent(block)}
                            {renderTooltip(block)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

      </div>

      {modalContainer
        ? createPortal(
            <>
              {isLegendOpen ? (
                <div
                  className={`rom-pad__modal rom-pad__modal--legend ${
                    isLegendOpen ? "rom-pad__modal--open" : ""
                  }`}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={legendId}
                  aria-describedby={legendDescriptionId}
                  id={legendDialogId}
                >
                  <button
                    type="button"
                    className="rom-pad__modal-backdrop"
                    aria-label="Close indicator legend"
                    onClick={handleCloseLegend}
                  />
                  <div className="rom-pad__modal-dialog rom-pad__modal-dialog--legend" role="document">
                    <button
                      type="button"
                      className="rom-pad__modal-close"
                      onClick={handleCloseLegend}
                      aria-label="Close indicator legend"
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                        <path
                          d="M6.75 6.75l10.5 10.5m0-10.5l-10.5 10.5"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    <div className="rom-pad__legend-modal">
                      <header className="rom-pad__legend-modal-header">
                        <span className="rom-pad__legend-modal-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" focusable="false">
                            <defs>
                              <linearGradient id={legendIconGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="var(--accent-500)" />
                                <stop offset="100%" stopColor="var(--accent-300)" />
                              </linearGradient>
                            </defs>
                            <circle cx="12" cy="12" r="11" fill={`url(#${legendIconGradientId})`} />
                            <path
                              d="M12 16.4v-3.75"
                              stroke="#0f1720"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                            <circle cx="12" cy="8.4" r="1.15" fill="#0f1720" />
                          </svg>
                        </span>
                        <div className="rom-pad__legend-modal-text">
                          <h2 id={legendId}>Indicator legend</h2>
                          <p id={legendDescriptionId}>
                            Colour-coded dots summarise each block metric. Use this guide to understand what each
                            colour communicates about performance.
                          </p>
                        </div>
                      </header>
                      <div className="rom-pad__legend-modal-content">
                        <div className="rom-pad__legend-groups">
                          {INDICATOR_LEGEND_GROUPS.map((group) => (
                            <div key={group.title} className="rom-pad__legend-group">
                              <span className="rom-pad__legend-group-title">{group.title}</span>
                              <div className="rom-pad__legend-items">
                                {group.items.map((item) => (
                                  <span key={item.label} className="rom-pad__legend-item">
                                    <span
                                      className="rom-pad__legend-dot"
                                      style={{ "--legend-color": item.color }}
                                      aria-hidden="true"
                                    />
                                    <span className="rom-pad__legend-label">{item.label}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="rom-pad__legend-modal-note">
                          Tip: Red indicators highlight metrics needing urgent attention, amber suggests caution, and
                          green signals blocks performing optimally.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {activeBlock ? (
                <div
                  className={`rom-pad__modal ${isModalOpen ? "rom-pad__modal--open" : ""}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="rom-pad-modal-title"
            >
            <button
              type="button"
              className="rom-pad__modal-backdrop"
              aria-label="Close block detail"
              onClick={handleCloseDetail}
            />
            <div className="rom-pad__modal-dialog" role="document">
              <button
                type="button"
                className="rom-pad__modal-close"
                onClick={handleCloseDetail}
                aria-label="Close block detail"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  focusable="false"
                >
                  <path
                    d="M6.75 6.75l10.5 10.5m0-10.5l-10.5 10.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
  
              <header className="rom-pad__modal-header">
                <div className="rom-pad__modal-header-main">
                  <span className="rom-pad__modal-finger">
                    Finger F{activeBlock.finger_label || activeBlock.finger_number || "—"}
                  </span>
                  <h2 id="rom-pad-modal-title">Block {formatText(activeBlock.block_id)}</h2>
                </div>
  
                <div className="rom-pad__modal-header-details">
                  <div className="rom-pad__modal-header-pills">
                    {renderIndicatorPill(activeBlock, "p_grade", {
                      className: "rom-pad__pill--detail rom-pad__pill--modal",
                      showLabel: true,
                    })}
                    {renderIndicatorPill(activeBlock, "product_classification", {
                      className: "rom-pad__pill--detail rom-pad__pill--modal",
                      showLabel: true,
                    })}
                  </div>
                  <div className="rom-pad__modal-header-stats">
                    {[
                      {
                        label: "Block status",
                        value: formatText(activeBlock.block_status),
                      },
                      {
                        label: "Tonnage",
                        value: (() => {
                          const tonnage = formatNumber(activeBlock.tonnage, {
                            maximumFractionDigits: 0,
                          });
                          return tonnage !== "—" ? `${tonnage} t` : tonnage;
                        })(),
                      },
                      {
                        label: "Hours remaining",
                        value: formatHours(activeBlock.hours_remaining),
                      },
                    ].map((stat) => (
                      <div key={stat.label} className="rom-pad__modal-stat">
                        <span>{stat.label}</span>
                        <strong>{stat.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
  
                {activeBlock.latest_update && (
                  <p className="rom-pad__modal-updated">
                    Updated {formatDate(activeBlock.latest_update)}
                  </p>
                )}
              </header>
  
              <div className="rom-pad__modal-sections">
                {(() => {
                  const sections = [
                    {
                      key: "geologist",
                      title: "Geologist inputs",
                      icon: (
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path
                            d="M4.5 14.25l7.5-7.5 2.75 2.75-7.5 7.5-3 1 1.25-3.75z"
                            fill="currentColor"
                            opacity="0.85"
                          />
                          <path
                            d="M13.75 6.5l2.25-2.25 2.75 2.75-2.25 2.25"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ),
                      fields: [
                        { label: "Block ID", value: formatText(activeBlock.block_id) },
                        {
                          label: "P-grade",
                          value: renderIndicatorPill(activeBlock, "p_grade", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Moisture",
                          value: renderIndicatorPill(activeBlock, "moisture", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        { label: "Slimes", value: formatText(activeBlock.slimes) },
                        {
                          label: "Ore classification",
                          value: renderIndicatorPill(activeBlock, "ore_classification", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Stockpile routing",
                          value: formatText(activeBlock.stockpile_routing),
                        },
                        {
                          label: "Geologist sign-off",
                          value: formatDate(activeBlock.geologist_date),
                        },
                      ],
                    },
                    {
                      key: "finger",
                      title: "Finger allocation",
                      icon: (
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <rect
                            x="3.25"
                            y="4"
                            width="17.5"
                            height="16"
                            rx="3"
                            ry="3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          <path
                            d="M7 8.5h10M7 12h10M7 15.5h6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      ),
                      fields: [
                        {
                          label: "Finger number",
                          value: formatText(
                            activeBlock.finger_label || activeBlock.finger_number
                          ),
                        },
                        {
                          label: "Block status",
                          value: formatText(activeBlock.block_status),
                        },
                        {
                          label: "Finger status",
                          value: formatText(activeBlockFinger?.status),
                        },
                        {
                          label: "Finger fill",
                          value: formatPercentage(activeBlockFinger?.fill),
                        },
                        {
                          label: "Allocation date",
                          value: formatDate(activeBlock.allocation_date),
                        },
                        {
                          label: "Hours remaining",
                          value: renderIndicatorPill(activeBlock, "hours_remaining", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Tonnage",
                          value: (() => {
                            const tonnage = formatNumber(activeBlock.tonnage, {
                              maximumFractionDigits: 0,
                            });
                            return tonnage !== "—" ? `${tonnage} t` : tonnage;
                          })(),
                        },
                      ],
                    },
                    {
                      key: "metallurgist",
                      title: "Metallurgist inputs",
                      icon: (
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path
                            d="M7 4h10l-3 5v6.5a2.5 2.5 0 01-5 0V9L7 4z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                            fill="none"
                          />
                          <path
                            d="M9 15.5h6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M9 12.5h6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      ),
                      fields: [
                        { label: "Product grade", value: formatText(activeBlock.product_grade) },
                        {
                          label: "Product classification",
                          value: renderIndicatorPill(activeBlock, "product_classification", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Mass pull %",
                          value: (() => {
                            const numeric = parseNumeric(activeBlock.mass_pull_percent);
                            if (Number.isFinite(numeric)) {
                              return `${formatNumber(numeric, { maximumFractionDigits: 1 })}%`;
                            }
                            return formatText(activeBlock.mass_pull_percent);
                          })(),
                        },
                        {
                          label: "Mass pull classification",
                          value: renderIndicatorPill(activeBlock, "mass_pull_classification", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Recovery",
                          value: formatRecovery(activeBlock.recovery),
                        },
                        {
                          label: "Recovery classification",
                          value: renderIndicatorPill(activeBlock, "recovery_classification", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Slimes classification",
                          value: renderIndicatorPill(activeBlock, "slimes_classification", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        {
                          label: "Slimes generating",
                          value: formatText(activeBlock.slimes_generating),
                        },
                        {
                          label: "Floatability",
                          value: renderIndicatorPill(activeBlock, "floatability", {
                            className: "rom-pad__pill--detail",
                          }),
                        },
                        { label: "Issues", value: formatText(activeBlock.issues) },
                        { label: "Notes", value: formatText(activeBlock.notes) },
                        {
                          label: "Metallurgist sign-off",
                          value: formatDate(activeBlock.metallurgist_date),
                        },
                      ],
                    },
                  ];
  
                  return sections.map((section) => (
                    <section key={section.key} className="rom-pad__modal-section">
                      <header className="rom-pad__modal-section-header">
                        <span className="rom-pad__modal-section-icon">{section.icon}</span>
                        <h3>{section.title}</h3>
                      </header>
                      <dl className="rom-pad__modal-list">
                        {section.fields.map((field) => (
                          <div key={field.label}>
                            <dt>{field.label}</dt>
                            <dd>{field.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  ));
                })()}
              </div>
            </div>
          </div>
        ) : null}

              {activeFinger && fingerModalData ? (
                <div
                  className={`rom-pad__modal rom-pad__modal--finger ${
                    isFingerModalOpen ? "rom-pad__modal--open" : ""
                  }`}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="rom-pad-finger-modal-title"
                >
                  <button
                    type="button"
                    className="rom-pad__modal-backdrop"
                    aria-label="Close finger detail"
                    onClick={handleCloseFingerDetail}
                  />
                  <div className="rom-pad__modal-dialog" role="document">
                    <button
                      type="button"
                      className="rom-pad__modal-close"
                      onClick={handleCloseFingerDetail}
                      aria-label="Close finger detail"
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                        <path
                          d="M6.75 6.75l10.5 10.5m0-10.5l-10.5 10.5"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>

                    <header className="rom-pad__modal-header">
                      <div className="rom-pad__modal-header-main">
                        <span className="rom-pad__modal-finger">
                          Finger F{fingerModalData.label}
                        </span>
                        <h2 id="rom-pad-finger-modal-title">Finger details</h2>
                      </div>

                      <div className="rom-pad__modal-header-details">
                        <div className="rom-pad__modal-header-pills">
                          <span
                            className={`rom-pad__finger-status rom-pad__finger-status--${fingerModalData.statusKey}`}
                          >
                            {fingerModalData.statusLabel}
                          </span>
                        </div>
                        <div className="rom-pad__modal-header-stats">
                          {fingerHeaderStats.map((stat) => (
                            <div key={stat.label} className="rom-pad__modal-stat">
                              <span>{stat.label}</span>
                              <strong>{stat.value}</strong>
                            </div>
                          ))}
                        </div>
                      </div>

                      {fingerModalData.latestUpdate ? (
                        <p className="rom-pad__modal-updated">
                          Updated {formatDate(fingerModalData.latestUpdate)}
                        </p>
                      ) : null}
                    </header>

                    <div className="rom-pad__modal-sections">
                      <section className="rom-pad__modal-section">
                        <header className="rom-pad__modal-section-header">
                          <span className="rom-pad__modal-section-icon">
                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                              <path
                                d="M5 7h14M5 17h14"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                              <circle
                                cx="9"
                                cy="7"
                                r="2.25"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                              <circle
                                cx="15"
                                cy="17"
                                r="2.25"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                            </svg>
                          </span>
                          <h3>Fill management</h3>
                        </header>

                        <div className="rom-pad__finger-fill-control">
                          <div className="rom-pad__finger-fill-header">
                            <div className="rom-pad__finger-fill-header-item">
                              <span className="rom-pad__finger-fill-label">Fill percentage</span>
                              <strong className="rom-pad__finger-fill-value">{fingerFillLabel}</strong>
                            </div>
                            <div className="rom-pad__finger-fill-summary">
                              <span>Hours remaining</span>
                              <strong>{fingerModalData.hoursTotalLabel || "—"}</strong>
                            </div>
                          </div>

                          {fingerOrderOptions.length ? (
                            <div className="rom-pad__finger-order-control">
                              <div className="rom-pad__finger-order-header">
                                <label htmlFor={activeFingerOrderSelectId}>Display order</label>
                                <span className="rom-pad__finger-order-count" aria-hidden="true">
                                  of {fingerOrderOptions.length}
                                </span>
                              </div>
                              {fingerOrderUpdatedLabel && (
                                <div className="rom-pad__finger-order-meta">
                                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="9"
                                      stroke="currentColor"
                                      strokeWidth="1.4"
                                      fill="none"
                                    />
                                    <path
                                      d="M12 7.5v4.5l3 1.5"
                                      stroke="currentColor"
                                      strokeWidth="1.4"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  <span>Last updated {fingerOrderUpdatedLabel}.</span>
                                </div>
                              )}
                              <div className="rom-pad__finger-order-select">
                                <select
                                  id={activeFingerOrderSelectId}
                                  value={
                                    activeFingerOrderPosition
                                      ? String(activeFingerOrderPosition)
                                      : fingerOrderOptions.length
                                      ? String(fingerOrderOptions[0])
                                      : ""
                                  }
                                  onChange={(event) => {
                                    const next = Number(event.target.value) - 1;
                                    if (!Number.isFinite(next)) {
                                      return;
                                    }
                                    handleFingerOrderSelectChange(activeFinger, next);
                                  }}
                                  disabled={fingerOrderSaving}
                                >
                                  {fingerOrderOptions.map((position) => (
                                    <option key={position} value={String(position)}>
                                      {position}
                                    </option>
                                  ))}
                                </select>
                                {fingerOrderSaving ? (
                                  <span className="rom-pad__finger-order-status">Saving…</span>
                                ) : null}
                              </div>
                            </div>
                          ) : null}

                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="0.1"
                            value={sliderValue}
                            onChange={handleFingerFillChange}
                            className="rom-pad__finger-fill-input"
                            style={fingerFillProgressStyle}
                            disabled={fingerFillSaving}
                            aria-label="Adjust fill percentage"
                          />

                          <div className="rom-pad__finger-fill-scale" aria-hidden="true">
                            <span>0%</span>
                            <span>50%</span>
                            <span>100%</span>
                          </div>

                          {fingerFillFeedback ? (
                            <p
                              className={`rom-pad__finger-fill-message rom-pad__finger-fill-message--${fingerFillFeedback.type}`}
                              role={fingerFillFeedback.type === "error" ? "alert" : "status"}
                            >
                              {fingerFillFeedback.message}
                            </p>
                          ) : null}

                          <div className="rom-pad__finger-fill-actions">
                            <button
                              type="button"
                              className="rom-pad__ghost-button"
                              onClick={handleFingerFillReset}
                              disabled={fingerFillSaving || !fingerFillDirty}
                            >
                              Reset
                            </button>
                            <button
                              type="button"
                              className="forms-button"
                              onClick={handleFingerFillSave}
                              disabled={fingerFillSaving || !fingerFillDirty}
                            >
                              {fingerFillSaving ? "Saving…" : "Save changes"}
                            </button>
                          </div>
                        </div>
                      </section>

                      <section className="rom-pad__modal-section">
                        <header className="rom-pad__modal-section-header">
                          <span className="rom-pad__modal-section-icon">
                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                              <path
                                d="M4.5 17.5l4.5-10 5 6.5 4-9"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                              <circle cx="4.5" cy="17.5" r="1.5" fill="currentColor" />
                              <circle cx="9" cy="7.5" r="1.5" fill="currentColor" />
                              <circle cx="13.5" cy="14" r="1.5" fill="currentColor" />
                              <circle cx="17.5" cy="5" r="1.5" fill="currentColor" />
                            </svg>
                          </span>
                          <h3>Plant routing</h3>
                        </header>
                        <dl className="rom-pad__modal-list">
                          {plantRoutingFields.map((field) => (
                            <div key={field.label}>
                              <dt>{field.label}</dt>
                              <dd>{field.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </section>

                      <section className="rom-pad__modal-section">
                        <header className="rom-pad__modal-section-header">
                          <span className="rom-pad__modal-section-icon">
                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                              <rect
                                x="3.25"
                                y="4"
                                width="17.5"
                                height="16"
                                rx="3"
                                ry="3"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                              <path
                                d="M7 8.5h10M7 12h10M7 15.5h6"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </span>
                          <h3>Assigned blocks</h3>
                        </header>
                        {activeFinger.blocks.length ? (
                          <ul className="rom-pad__finger-block-list">
                            {activeFinger.blocks.map((block) => {
                              const tonnage = formatNumber(block.tonnage, {
                                maximumFractionDigits: 0,
                              });
                              const tonnageLabel = tonnage !== "—" ? `${tonnage} t` : tonnage;

                              return (
                                <li key={block.block_id}>
                                  <span className="rom-pad__finger-block-id">
                                    {formatText(block.block_id)}
                                  </span>
                                  <span className="rom-pad__finger-block-tonnage">{tonnageLabel}</span>
                                  <span className="rom-pad__finger-block-status">
                                    {formatText(block.block_status)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="rom-pad__finger-empty">No blocks assigned.</p>
                        )}
                      </section>
                    </div>
                  </div>
                </div>
              ) : null}
            </>,
            modalContainer
          )
        : null}
    </div>
  );
};

export default RomPad;
